// BarChart.js
// Renders the chart it is given. Saved display settings (title, axes, colours)
// are applied by the caller — applyChartSettings, with the settings of the
// version the shown run executed — so this component reads nothing itself.
//
// The figure (traces + layout) comes from a pure builder so the combined
// chart's whole-image export can draw this chart without rendering it.
import React from 'react';
import Plot from 'react-plotly.js';
import { useTheme } from '@mui/material/styles';
import ChartCard from './ChartCard';
import ChartExportButton from './ChartExportButton';
import { resolveSeriesLabels } from '../../utils/chartSeries';
import { downloadChartPng, exportTitle } from '../../utils/chartExport';
import {
  CHART_CONFIG,
  CHART_SURFACE_RING,
  barGaps,
  barHoverTemplate,
  buildAxes,
  buildLayout,
  hoverValues,
  seriesColor,
} from '../../utils/chartTheme';

export const buildBarFigure = (chartData, { ink, themePalette }) => {
  const palette = chartData.colors || themePalette;
  const showGrid = chartData.grid !== false;
  const showLegend = chartData.show_legend !== false;
  const isHorizontal = chartData.horizontal === true;
  const isStacked = chartData.stacked === true;
  const { bargap, bargroupgap } = barGaps({ barWidth: chartData.bar_width, stacked: isStacked });

  // Group data by groups if present. A bar that belongs to no group carries
  // `group: null`, and null used as an object key becomes the text "null",
  // which the legend would then show. It is keyed by the empty name instead,
  // which resolveSeriesLabels below treats as a placeholder: a lone legend is
  // hidden, and among several series it reads "Series N".
  const groups = {};
  chartData.elements.forEach(el => {
    const groupName = el.group ?? '';
    if (!groups[groupName]) {
      groups[groupName] = { labels: [], values: [] };
    }
    groups[groupName].labels.push(el.label);
    groups[groupName].values.push(el.value);
  });

  // The group keys ARE this chart's series list, so they are what the legend
  // enumerates and what the placeholder fallback has to rewrite.
  const groupNames = Object.keys(groups);
  const { labels: seriesLabels, hideLegend } = resolveSeriesLabels(groupNames);

  const data = groupNames.map((group, idx) => ({
    type: 'bar',
    name: seriesLabels[idx],
    x: isHorizontal ? groups[group].values : groups[group].labels,
    y: isHorizontal ? groups[group].labels : groups[group].values,
    orientation: isHorizontal ? 'h' : 'v',
    marker: {
      color: seriesColor(palette, idx),
      // Stacked segments touch, so a hairline of the card between them is what
      // makes two segments read as two rather than as one long bar.
      ...(isStacked ? { line: { color: CHART_SURFACE_RING, width: 1 } } : {})
    },
    customdata: hoverValues(groups[group].values, chartData.y_label, chartData.y_unit),
    hovertemplate: barHoverTemplate(isHorizontal)
  }));

  const layout = buildLayout({
    // `x_label` names the category and `y_label` the measure whichever way the
    // bars run; the rotations stay with the axis the settings dialog names, so
    // "X-Axis Label Rotation" always turns the labels along the bottom.
    axes: buildAxes({
      categoryLabel: chartData.x_label,
      measureLabel: chartData.y_label,
      categoryUnit: chartData.x_unit,
      measureUnit: chartData.y_unit,
      categoryRotation: (isHorizontal ? chartData.y_axis_rotation : chartData.x_axis_rotation) || 0,
      measureRotation: (isHorizontal ? chartData.x_axis_rotation : chartData.y_axis_rotation) || 0,
      showGrid,
      horizontal: isHorizontal,
      autoScale: chartData.auto_scale,
      // Bar labels are always categories. Left untyped, the chart library reads
      // number-like labels such as binder "2024888" as a number line.
      categoryMetadata: { scale: 'categorical' },
      ink
    }),
    // An explicit `show_legend: false` and a lone placeholder series each mean
    // "no legend"; either one alone is enough to drop it.
    showLegend: showLegend && !hideLegend,
    ink,
    barmode: isStacked ? 'relative' : 'group',
    bargap,
    bargroupgap
  });

  return { data, layout };
};

// `showTitle: false` is for a frame that prints the title itself (a View
// card); the export still carries it. `ancestorTitles` are the titles of the
// combined charts this one sits inside, printed on the export as a prefix.
function BarChartComponent({ chartData, showTitle = true, ancestorTitles = [] }) {
  const theme = useTheme();
  // The series palette and the reading ink both come from the app theme, so the
  // chart layer holds no colour of its own.
  const ink = { primary: theme.palette.text.primary, secondary: theme.palette.text.secondary };
  const { data, layout } = buildBarFigure(chartData, { ink, themePalette: theme.palette.custom.chartColors });
  const title = exportTitle(ancestorTitles, chartData.title);

  return (
    <ChartCard
      title={showTitle ? chartData.title : null}
      ink={ink}
      actions={<ChartExportButton onExport={() => downloadChartPng({ data, layout, title })} />}
    >
      <Plot
        data={data}
        layout={layout}
        config={CHART_CONFIG}
        style={{ width: '100%', height: 'calc(100% - 50px)' }}
        useResizeHandler={true}
      />
    </ChartCard>
  );
}

export default BarChartComponent;
